import React from 'react';
import { GraduationCap, Brain, Trophy, Users, ShieldCheck, ExternalLink } from 'lucide-react';
import { ELVTR_CERTIFICATE_URL } from '../lib/siteConfig';

/**
 * TrustStrip — narrow credibility band that sits directly under the hero.
 * All claims are factual (sourced from CredibilitySection.js) — no invented credentials.
 * The ELVTR item links to the actual verifiable certificate PDF.
 */
const items = [
  { Icon: GraduationCap, text: 'M.Ed., Wilmington University' },
  {
    Icon: Brain,
    text: 'ELVTR Sports Psychology Certified',
    href: ELVTR_CERTIFICATE_URL,
    verifyLabel: 'Verified · View certificate',
  },
  { Icon: Trophy,        text: 'Asst. Head Coach, MKA · Non-Public B State Champions' },
  { Icon: Users,         text: 'Middle School → College Athletes' },
  { Icon: ShieldCheck,   text: 'Performance Training (Not Therapy)' },
];

function trackTrustClick(label) {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'trust_strip_click', { label });
    }
  } catch {
    /* analytics is best-effort */
  }
}

export const TrustStrip = () => {
  return (
    <section
      id="trust"
      className="relative bg-[#0a0d0e] border-y border-white/10 py-6"
      aria-label="Credibility highlights"
      data-testid="trust-strip"
    >
      <div className="max-w-[1200px] mx-auto px-5 sm:px-6">
        <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 sm:gap-x-10">
          {items.map(({ Icon, text, href, verifyLabel }) => {
            const testId = `trust-item-${text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`;
            const baseRow = (
              <>
                <Icon className="h-3.5 w-3.5 text-[#4FBCC7] shrink-0" />
                <span>{text}</span>
                {href && (
                  <ExternalLink
                    className="h-3 w-3 text-[#4FBCC7]/70 group-hover:text-[#4FBCC7]"
                    aria-hidden="true"
                  />
                )}
              </>
            );

            return (
              <li key={text} className="flex items-center">
                {href ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackTrustClick(text)}
                    className="group flex items-center gap-2 text-white/80 hover:text-white text-[12.5px] sm:text-[13px] font-medium underline decoration-[#4FBCC7]/40 decoration-1 underline-offset-[5px] hover:decoration-[#4FBCC7]"
                    data-testid={testId}
                    aria-label={verifyLabel || text}
                    title={verifyLabel || text}
                  >
                    {baseRow}
                  </a>
                ) : (
                  <div
                    className="flex items-center gap-2 text-white/70 text-[12.5px] sm:text-[13px] font-medium"
                    data-testid={testId}
                  >
                    {baseRow}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default TrustStrip;
